import React, { useState } from 'react';
import { GallerySectionProps } from '../types/components';
import { GalleryImage } from '../types/images';

// Gallery data with all 36 jewelry images from public folder (12 Gold + 12 Silver + 12 Kundan)
const galleryImages: GalleryImage[] = [
  // Gold images (1-12)
  { id: 'gallery-gold-1', url: '/Gold/1.jpg', alt: 'Gold jewelry 1' },
  { id: 'gallery-gold-2', url: '/Gold/2.jpg', alt: 'Gold jewelry 2' },
  { id: 'gallery-gold-3', url: '/Gold/3.jpg', alt: 'Gold jewelry 3' },
  { id: 'gallery-gold-4', url: '/Gold/4.jpg', alt: 'Gold jewelry 4' },
  { id: 'gallery-gold-5', url: '/Gold/5.jpg', alt: 'Gold jewelry 5' },
  { id: 'gallery-gold-6', url: '/Gold/6.jpg', alt: 'Gold jewelry 6' },
  { id: 'gallery-gold-7', url: '/Gold/7.jpg', alt: 'Gold jewelry 7' },
  { id: 'gallery-gold-8', url: '/Gold/8.jpg', alt: 'Gold jewelry 8' },
  { id: 'gallery-gold-9', url: '/Gold/9.jpg', alt: 'Gold jewelry 9' },
  { id: 'gallery-gold-10', url: '/Gold/10.jpg', alt: 'Gold jewelry 10' },
  { id: 'gallery-gold-11', url: '/Gold/11.jpg', alt: 'Gold jewelry 11' },
  { id: 'gallery-gold-12', url: '/Gold/12.jpg', alt: 'Gold jewelry 12' },
  
  // Silver images (13-24)
  { id: 'gallery-silver-1', url: '/Silver/1.jpg', alt: 'Silver jewelry 1' },
  { id: 'gallery-silver-2', url: '/Silver/2.jpg', alt: 'Silver jewelry 2' },
  { id: 'gallery-silver-3', url: '/Silver/3.jpg', alt: 'Silver jewelry 3' },
  { id: 'gallery-silver-4', url: '/Silver/4.jpg', alt: 'Silver jewelry 4' },
  { id: 'gallery-silver-5', url: '/Silver/5.jpg', alt: 'Silver jewelry 5' },
  { id: 'gallery-silver-6', url: '/Silver/6.jpg', alt: 'Silver jewelry 6' },
  { id: 'gallery-silver-7', url: '/Silver/7.jpg', alt: 'Silver jewelry 7' },
  { id: 'gallery-silver-8', url: '/Silver/8.jpg', alt: 'Silver jewelry 8' },
  { id: 'gallery-silver-9', url: '/Silver/9.jpg', alt: 'Silver jewelry 9' },
  { id: 'gallery-silver-10', url: '/Silver/10.png', alt: 'Silver jewelry 10' },
  { id: 'gallery-silver-11', url: '/Silver/11.jpg', alt: 'Silver jewelry 11' },
  { id: 'gallery-silver-12', url: '/Silver/12.jpg', alt: 'Silver jewelry 12' },
  
  // Kundan images (25-36)
  { id: 'gallery-kundan-1', url: '/Kundan/1.jpg', alt: 'Kundan jewelry 1' },
  { id: 'gallery-kundan-2', url: '/Kundan/2.jpg', alt: 'Kundan jewelry 2' },
  { id: 'gallery-kundan-3', url: '/Kundan/3.jpg', alt: 'Kundan jewelry 3' },
  { id: 'gallery-kundan-4', url: '/Kundan/4.jpg', alt: 'Kundan jewelry 4' },
  { id: 'gallery-kundan-5', url: '/Kundan/5.jpg', alt: 'Kundan jewelry 5' },
  { id: 'gallery-kundan-6', url: '/Kundan/6.jpg', alt: 'Kundan jewelry 6' },
  { id: 'gallery-kundan-7', url: '/Kundan/7.jpg', alt: 'Kundan jewelry 7' },
  { id: 'gallery-kundan-8', url: '/Kundan/8.jpg', alt: 'Kundan jewelry 8' },
  { id: 'gallery-kundan-9', url: '/Kundan/9.jpg', alt: 'Kundan jewelry 9' },
  { id: 'gallery-kundan-10', url: '/Kundan/10.jpg', alt: 'Kundan jewelry 10' },
  { id: 'gallery-kundan-11', url: '/Kundan/11.jpg', alt: 'Kundan jewelry 11' },
  { id: 'gallery-kundan-12', url: '/Kundan/12.jpg', alt: 'Kundan jewelry 12' },
];

export const GallerySection: React.FC<GallerySectionProps> = ({ onImageClick }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const IMAGES_PER_PAGE = 8;

  // Calculate total pages
  const totalPages = Math.ceil(galleryImages.length / IMAGES_PER_PAGE);

  // Get current page images
  const getCurrentPageImages = (): GalleryImage[] => {
    const startIndex = (currentPage - 1) * IMAGES_PER_PAGE;
    const endIndex = startIndex + IMAGES_PER_PAGE;
    return galleryImages.slice(startIndex, endIndex);
  };

  // Navigation handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const currentImages = getCurrentPageImages();
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <section id="gallery" className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Tagline */}
        <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase text-center mb-4">
          Visual Excellence
        </p>

        {/* Title */}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-center text-white mb-12">
          The Jewelry Gallery
        </h2>

        {/* Gallery Grid with Navigation */}
        <div className="relative px-12">
          {/* Left Arrow */}
          {!isFirstPage && (
            <button
              onClick={goToPreviousPage}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/90 hover:bg-yellow-500 text-white hover:text-black p-2 md:p-3 rounded-full transition-all duration-300 shadow-lg"
              aria-label="Previous images"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 md:h-6 md:w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Image Grid - 4 columns x 2 rows */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 transition-all duration-500 ease-in-out">
            {currentImages.map((image) => (
              <div
                key={image.id}
                className="relative aspect-square overflow-hidden rounded-2xl cursor-pointer group shadow-xl"
                onClick={() => onImageClick(image.url)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                {/* Hover overlay with zoom icon */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                    />
                  </svg>
                </div>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {!isLastPage && (
            <button
              onClick={goToNextPage}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/90 hover:bg-yellow-500 text-white hover:text-black p-2 md:p-3 rounded-full transition-all duration-300 shadow-lg"
              aria-label="Next images"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 md:h-6 md:w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
